<template>
  <div class="ui-prices-chart">
    <div class="ui-prices-chart__actions">
      <slot />
      <UIButton
        v-for="range in RANGES"
        :key="range"
        :label="range"
        variant="secondary"
        :fill="buttonFill(range)"
        size="xs"
        type="button"
        @click="handleChangeRange(range)"
      />
    </div>
    <EChart
      v-if="lastDate"
      ref="chartRef"
      :option
      :selected-index
      @click:item="handleClickItem"
    />
    <UILayoutNoData
      v-else
      hide-question-icon
      inline
      class="ui-prices-chart__no-data"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, useTemplateRef, watch } from 'vue'
import { DateTime } from 'luxon'
import { useLocalStorage } from '@vueuse/core'
import { maxBy, minBy } from 'lodash'

import { EChartOption } from 'echarts'
import { ChartPricesRange } from './utils/enums'

import { BLUR_DIGITAL_VALUES } from '@/const/storage'

import { stringToDateTime } from '@/helpers/dates'

import EChart from './EChart.vue'
import { UILayoutNoData } from '@ui/core'
import { UIButton } from '@ui/buttons'

const FRACTION_DIGITS_MIN = 2

const RANGES = Object.values(ChartPricesRange)

type Props = {
  prices: EChartOption.SeriesLine[]

  nameFormatter?: (data: string) => string
  valueFormatter?: (data: number, fractionDigits?: number) => string
}

const props = defineProps<Props>()

const selectedIndex = defineModel<number>('index')
const currentRange = defineModel<ChartPricesRange>('range', {
  default: ChartPricesRange.YEAR,
})

const blurDigitalValues = useLocalStorage(BLUR_DIGITAL_VALUES, false)

const chartRef = useTemplateRef('chartRef')

const instance = computed(() => chartRef.value?.getInstance())

const fractionDigits = computed(() => {
  const data = props.prices.reduce((acc, price) => {
    const items = price.data as any
    if (!Array.isArray(items) || items.length === 0) {
      return acc
    }
    const maxItem = maxBy(items, item => item.value[1])
    const minItem = minBy(items, item => item.value[1])
    if (!maxItem || !minItem) {
      return acc
    }
    const max = maxItem.value[1]
    const min = minItem.value[1]
    if (typeof min === 'number' && (acc.min === undefined || min < acc.min)) acc.min = min
    if (typeof max === 'number' && (acc.max === undefined || max > acc.max)) acc.max = max
    return acc
  }, { min: undefined, max: undefined } as { min: number | undefined, max: number | undefined })
  const diff = (data.max || 0) - (data.min || 0)
  if (diff < 1) return 4
  if (diff < 10) return 3
  return FRACTION_DIGITS_MIN
})

const option = computed(() => {
  return {
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'line',
      },
      formatter: (
        params: EChartOption.Tooltip.Format | EChartOption.Tooltip.Format[],
      ) => {
        let date: string = ''
        const result = (params as EChartOption.Tooltip.Format[]).map(param => {
          const { color, name, seriesName, value } =
            param as EChartOption.Tooltip.Format
          if (!date) date = `${name}`
          const caption = props.nameFormatter?.(seriesName || '') || seriesName
          const amount = (value as any)?.[1] || 0
          return `<div style="color:${color}">${caption}: <span class="number-node">${valueFormatter(amount)}</span></div>`
        })
        return `${date}${result.join('')}`
      },
    } as EChartOption.BaseTooltip,
    xAxis: {
      type: 'time',
      axisLabel: {
        interval: 0,
        lineHeight: 36,
        margin: 12,
        rotate: 45,
      },
    } as EChartOption.XAxis,
    yAxis: {
      position: 'right',
      scale: true,
      axisLabel: {
        formatter: (value: number) => {
          return blurDigitalValues.value ? '? ??? ???' : valueFormatter(value)
        },
      },
    } as EChartOption.YAxis,
    series: [
      {
        type: 'scatter',
        datasetIndex: 0,
      },
      ...props.prices.map((price, index) => ({
        type: 'line',
        showSymbol: false,
        ...price,
        datasetIndex: index + 1,
      })),
    ],
    grid: {
      height: '100%',
      top: '2%',
      right: '0%',
      bottom: '0%',
      left: '0%',
      containLabel: true,
    },
    dataZoom: [
      {
        type: 'inside',
        realtime: true,
        zoomOnMouseWheel: false,
      },
    ],
  }
})

const lastDate = computed(() =>
  DateTime.max(
    ...(props.prices
      .filter(item => item.data?.length)
      .map(item => {
        const el = item.data?.at(-1)
        return stringToDateTime((el as any).value[0])
      }) as DateTime[]),
  ),
)

const buttonFill = (range: ChartPricesRange) =>
  currentRange.value === range ? undefined : 'light'

const handleClickItem = (index: number) => {
  selectedIndex.value = index
}

const handleChangeRange = (range: ChartPricesRange) => {
  currentRange.value = range
}

const valueFormatter = (value: number) => {
  let maxFractionDigits = value.toString().split('.')[1]?.length || 0
  if (maxFractionDigits > fractionDigits.value) {
    maxFractionDigits = fractionDigits.value
  }
  if (maxFractionDigits < FRACTION_DIGITS_MIN) {
    maxFractionDigits = FRACTION_DIGITS_MIN
  }
  return props.valueFormatter?.(value, maxFractionDigits) || value
}

watch(
  () => [currentRange.value, instance.value, props.prices],
  async ([range, instance]) => {
    if (!instance) return
    let startValue
    let start
    switch (range) {
      case ChartPricesRange.WEEK:
        startValue = lastDate.value?.minus({ weeks: 1 })
        break
      case ChartPricesRange.MONTH:
        startValue = lastDate.value?.minus({ months: 1 })
        break
      case ChartPricesRange.MONTH6:
        startValue = lastDate.value?.minus({ months: 6 })
        break
      case ChartPricesRange.YTD:
        startValue = lastDate.value?.startOf('year')
        break
      case ChartPricesRange.YEAR:
        startValue = lastDate.value?.minus({ years: 1 })
        break
      case ChartPricesRange.YEAR5:
        startValue = lastDate.value?.minus({ years: 5 })
        break
      default:
        start = 0
        break
    }
    await nextTick()
    // @ts-expect-error - instance is an EChartInstance
    instance.dispatchAction({
      type: 'dataZoom',
      startValue,
      start,
    })
  },
  { immediate: true },
)
</script>

<style scoped>
.ui-prices-chart {
  @apply h-full;
  @apply relative;
  @apply flex flex-col;
  @apply gap-1;

  &__actions {
    @apply flex items-center;
    @apply gap-2;
    @apply z-10;
  }

  .e-chart {
    @apply flex-auto;
  }

  &__no-data {
    @apply flex-auto;
  }
}
</style>
